import {makeStyles} from "@material-ui/core/styles";
import {Paper, Box, Grid, Typography, IconButton} from "@material-ui/core";
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel';
import ViewComfyIcon from '@material-ui/icons/ViewComfy';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import clsx from "clsx";
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import HelloWhiteIcon from '../assets/icons/hello_white.png';
import GuidelinesWhiteIcon from '../assets/icons/guidelines_white.png';
import InstructionsWhiteIcon from '../assets/icons/instructions_white.png';
import {useState, useRef, useEffect} from "react";
import StepNavigator from '../components/StepNavigator';
import DosAndDonts from "../components/DosAndDonts";

const useStyles = makeStyles((theme) => ({
    container: {
        height: '100%',
        marginTop: 16,
        borderRadius: theme.shape.borderRadius,
        flex: '1',
        display: 'flex',
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    },
    pageNavigation: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'stretch',
        width: 78,
        justifyContent: 'center',
        '& > div': {
            display: 'flex',
            overflow: 'hidden',
            justifyContent: 'center',
            height: 72,
            alignItems: 'center',
            '& > .MuiTypography-root': {
                flex: '1',
            }
        },
        transition: theme.transitions.create('all', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    pageNavigationActive: {
        width: 250,
        transition: theme.transitions.create('all', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    navigationToggle: {
        position: 'absolute',
        top: 16, right: -16,
        height: 16,
        width: 16,
        padding: 16,
        background: '#000',
        transition: theme.transitions.create('transform', {
            easing: theme.transitions.easing.easeIn,
            duration: theme.transitions.duration.short,
        }),
    },
    navigationToggleActive: {
        transform: 'rotate(180deg)',
        transition: theme.transitions.create('transform', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.short,
        }),
    },
    pageContent: {
        padding: `16px !important`,
        background: '#424a51',
        width: '100%',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 215px)',
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
    },
    button: {
        display: 'block',
        padding: '24px 16px',
        textAlign: 'center',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: theme.shape.borderRadius,
        fontSize: 16,
        '&:hover': {
            background: '#434A51',
        },
        '&::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            width: 0,
            height: '100%',
            top: 0,
            right: 0,
            transition: theme.transitions.create('all', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        transition: theme.transitions.create('all', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    buttonActive: {
        background: '#434A51',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        '&:hover': {
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
        },
        '&::after': {
            right: -16,
            background: '#434A51',
            width: 16,
        },
        transition: theme.transitions.create('all', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },

}));

const ICON_LIST = [
    {
        icon: <CreateIcon />,
        title: "Edit",
        text: ` This button lets you edit a picture to perform operations like cropping, zooming, flipping or rotating.
        To save your changes, you need to click on the button ‘Done’ in the edit window.`
    },
    {
        icon: <DeleteIcon style={{fill: "tomato"}} />,
        title: "Delete",
        text: "This button lets you delete the picture in the main view."
    },
    {
        icon: <ViewCarouselIcon />,
        title: "View as Carousel",
        text: "This button allows you to change the viewing type of the pictures from grid to list navigation."
    },
    {
        icon: <ViewComfyIcon />,
        title: "View as Grid",
        text: "This button allows you to change the viewing type of the pictures from list navigation to grid."
    },
    {
        icon: <div style={{height: 16, width: 16, borderRadius: '100%', background: 'yellow'}} />,
        title: "Info icon",
        text: "The yellow dot means that the item has not been viewed yet or is empty. This helps to keep track of the progress."
    }
]


const usePageStyles = makeStyles((theme) => ({
    root: {
        width: '80%',
        height: '100%',
        maxWidth: 1000,
        margin: 'auto',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        '& .MuiTypography-h3': {
            marginBottom: theme.spacing(4),
            fontWeight: 700,
            textAlign: 'center',
            display: 'block',
            width: '100%',
            '@media (max-height:850px)': {
                marginBottom: theme.spacing(2),
                fontSize: '2rem'
            },
            '@media (max-height:750px)': {
                marginBottom: theme.spacing(0.5),
                fontSize: '1.5rem'
            },
        },
        '& .MuiTypography-h5': {
            '@media (max-height:850px)': {
                marginBottom: theme.spacing(2),
                fontSize: '1.25rem'
            },
            '@media (max-height:750px)': {
                marginBottom: theme.spacing(0.5),
                fontSize: '1rem'
            },
        },
        '& > *': {
            marginBottom: theme.spacing(2),
            '@media (max-height:850px)': {
                marginBottom: theme.spacing(1),
                fontSize: 12,
            },
            '@media (max-height:750px)': {
                marginBottom: theme.spacing(0.5),
            },
        },
        '& li': {
            marginBottom: theme.spacing(2),
            '@media (max-height:850px)': {
                marginBottom: theme.spacing(1),
            },
        },
        '& .MuiFormControlLabel-label': {
            fontWeight: 700
        }
    }
}));
const RenderPage = ({page, tokenType, checked, checkEnabled, setCheckEnabled, setChecked}) => {
    const scrollableRef = useRef();
    const classes = usePageStyles();

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (!checkEnabled)
            setCheckEnabled(bottom);
    };

    useEffect(() => {
        if (scrollableRef.current) {
            const {scrollHeight, clientHeight} = scrollableRef.current;
            if (scrollHeight < clientHeight) {
                // no scrollbar =>
                setCheckEnabled(true);
            }
        }

    }, [scrollableRef, setCheckEnabled])

    switch (page) {
        case 0:
            return (
                <div className={classes.root}>
                    <Typography variant="h3" >Welcome to Mazingday</Typography>
                    <Typography>
                        Welcome and thank you for joining our Mazing adventure! We are thrilled to count you among our partners and cannot wait to open new doors by your side.
                    </Typography>
                    <Typography>
                        Our mobile application is a platform where you will display your activities, products and offers. <br />
                        By joining MazingDay’s network, you commit, just as we do, to provide the best experience possible to our customers.
                    </Typography>
                    <Typography  style={{fontWeight: 700}}>
                        Here are some guidelines to help us all reach this goal:
                    </Typography>
                    <Paper style={{padding: 16 ,flexGrow: '1', minHeight: 200, overflowY: 'scroll'}} ref={scrollableRef} onScroll={handleScroll}>
                        <Typography >
                            <ul >
                                <li >
                                    We require you to have an up-to-date regulatory authorization, license, or certification. It is your sole responsibility to fulfill the legal requirements thethat are required to operate your business.
                                </li>
                                <li >
                                    On the same note, you are expected to be fully covered by any appropriate insurance required for operating your business. Any accident, damage or injury caused to a visitor during an activity suggested by the app must be covered.</li>
                                <li >
                                    When registering as a Provider with Mazingday, you will be prompted to upload pictures illustrating your service or activity. Please make sure that you possess all intellectual property rights, publicity rights, and contractual rights.</li>
                                <li >
                                    Of course, the description of services or activities provided shall be true and up-to-date. Please always make sure that you possess all rights and authority to promote activities or services on the app. In the event that a service or an activity changes for any reason, please contact our Purchasing Department to modify your information. Mazingday will not be held accountable for any lack of information, inaccuracy of the information or change of any kind related to the activities suggested.
                                    Always keep in mind that the more accurate your description, the better customer satisfaction we get!</li>
                                <li > Mazingday might allow its customers to evaluate the activity they experienced according to the description displayed on the app, by means of rating or reviewing. We expect it to be a useful tool for you to get feedback from your visitors.</li>
                                <li >
                                    Even if any grief resulting from your relationship must be resolved exclusively between you and the customers, we expect appropriate behaviour or language from you, activity Provider, just as we do from our users. </li>
                            </ul>
                        </Typography>
                    </Paper>
                    <br/>
                    <Typography>
                        Don’t forget that we are partners, which means that our relationship is based on trust, mutual understanding and collaboration. Please feel free to contact us for any questions, queries or issues... we will always find a way to sort it out together!

                    </Typography>
                    <Typography >
                        You can contact your dedicated team at: <b>uae.activities@mazingday.com</b>
                    </Typography>
                    <Paper style={{width: '100%', padding: '4px 16px', }}>
                        <FormControlLabel
                            key={`question_formcontrol`}
                            control={
                                <Checkbox
                                    disabled={!checkEnabled}
                                    checked={checked}
                                    onChange={(e) => setChecked(e.target.checked)}
                                    name={`question`}
                                    color="primary"
                                />
                            }
                            label="I have read and understood all the guidelines."
                        />
                    </Paper>
                </div >
            )
        case 1:
            return <DosAndDonts />
        case 2:
            return (
                <Grid container spacing={2} style={{height: 'calc(100% + 16px)'}} alignItems="stretch" justifyContent="center">
                    <Grid item xs={8}>
                        <Paper style={{padding: 16, height: '100%'}}>
                            <Typography variant="h5">Activity</Typography>
                            <Typography>

                                <ul>

                                    <li>
                                        Based on the information provided on the phone call and from trusted online sources (such as your official website), we have listed down the services that are delivered or the activities that take place in your branch(es).
                                    </li>
                                    <li>
                                        Please select each activity and upload at least 3 relevant pictures to illustrate the activity.
                                    </li>
                                    {tokenType === 'PROVIDER' && <li>
                                        If you have multiple branches, please make sure that you select the activity and also the branches where it takes place. Then, please upload the relevant pictures.
                                    </li>}
                                    {tokenType === 'PROVIDER' && <li>
                                        If you have multiple branches and an activity is common accross all of them, please select ‘All branches’ in the branch filter with the respective activity in the activity filter. Then, proceed with the upload of the relevant pictures.
                                    </li>}
                                </ul>
                            </Typography>
                            <Typography variant="h5">Branch</Typography>
                            <Typography>
                                <ul>
                                    <li>
                                        The branch is the place where you welcome your public. Please upload at least 3 pictures to showcase the atmosphere of the place. Showing the front of your branch is also useful for customers to make sure that they are in the right place.
                                    </li>
                                    <li>
                                        Please upload the trade license of your branch so that we can make sure that you are legally operating in your area.
                                    </li>
                                    {tokenType === 'PROVIDER' && <li>
                                        If you have multiple branches, please select the right branch in the branch filter and upload the pictures of your branch and the respective trade license.
                                    </li>}
                                </ul>
                            </Typography>

                            <Typography variant="h5" gutterBottom>Overview</Typography>
                            <Typography>
                                <ul>
                                    <li>
                                        The Overview page displays all the pictures of the branch, the activities as well as the logo and the trade license.
                                    </li>
                                    <li>
                                        Please navigate through the activities and branches in the left before confirming your submission.
                                    </li>
                                    {tokenType === 'PROVIDER' && <li>
                                        If you have multiple branches, under the activities selection, please select the respective branch(es) or select ‘All branches’ to have an overview.
                                    </li>}
                                    <li>
                                        If you wish to modify any activity or branch, you can navigate to that page by clicking on the respective tab.
                                    </li>
                                </ul>
                            </Typography>

                        </Paper>
                    </Grid>
                    <Grid item xs={4}>
                        <Paper style={{padding: 16, height: '100%'}}>
                            <List >
                                {
                                    ICON_LIST.map((icon, i) => (
                                        <ListItem key={i}>
                                            <ListItemIcon>
                                                {icon.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={icon.text}

                                            />
                                        </ListItem>
                                    ))
                                }
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            )
        default:
            return null;
    }
}

export default function GuidelinesPage({onNext, data}) {
    const classes = useStyles();
    const [navigationExpanded, setNavigationExpanded] = useState(false);
    const [selectedPage, setSelectedPage] = useState(0);

    const [hasSeenContent, setHasSeenContent] = useState(false);
    const [hasReadGuideliens, setHasReadGuidelines] = useState(false);

    useEffect(() => {
        if (data?.completedSteps?.includes(0)) {
            setHasSeenContent(true);
            setHasReadGuidelines(true);
        }

    }, [data])

    const handleNextStep = () => {
        if (selectedPage < 2) {
            setSelectedPage(selectedPage + 1);
            return;
        }
        onNext();
    }

    const handlePrevStep = () => {
        setSelectedPage(Math.max(0, selectedPage - 1));
    }

    return (
        <>
            <Paper className={classes.container}>
                <Box className={clsx(classes.pageNavigation, {[classes.pageNavigationActive]: navigationExpanded})} >
                    <div
                        onClick={() => setSelectedPage(0)}
                        className={clsx(classes.button, {[classes.buttonActive]: selectedPage === 0})}>
                        <img src={HelloWhiteIcon} alt="Hello" /> {navigationExpanded && <Typography>Welcome</Typography>}
                    </div>
                    <div
                        onClick={() => hasReadGuideliens && setSelectedPage(1)}
                        className={clsx(classes.button, {[classes.buttonActive]: selectedPage === 1})}>
                        <img src={GuidelinesWhiteIcon} alt="guidelines" />
                        {navigationExpanded && <Typography>Do's and Dont's</Typography>}
                    </div>
                    <div
                        onClick={() => hasReadGuideliens && setSelectedPage(2)}
                        className={clsx(classes.button, {[classes.buttonActive]: selectedPage === 2})}>
                        <img src={InstructionsWhiteIcon} alt="instructions" />
                        {navigationExpanded && <Typography>Usage guidelines</Typography>}
                    </div>
                    <IconButton className={clsx(classes.navigationToggle, {[classes.navigationToggleActive]: navigationExpanded})}
                        onClick={() => setNavigationExpanded(!navigationExpanded)}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
                <Box className={classes.pageContent}>
                    <RenderPage page={selectedPage}
                        checked={hasReadGuideliens}
                        setChecked={setHasReadGuidelines}
                        checkEnabled={hasSeenContent}
                        setCheckEnabled={setHasSeenContent} tokenType={data?.tokenType} />
                </Box>
            </Paper >
            <StepNavigator onNext={handleNextStep} onPrev={handlePrevStep} disabled={!hasReadGuideliens} />
        </>
    );
}
